// Modal «Contenido del kit»: cada componente con su ficha y el ahorro contra comprarlos por separado.
import { useMemo } from 'react';
import { CreditCard, Headphones, Package, Router, Smartphone, type LucideIcon } from 'lucide-react';
import { Button, Modal } from '@/kit';
import { cn } from '@/lib/cn';
import { formatMXN } from '@/shared/lib/format/money';
import { itemDisplayName } from '@/shared/lib/format/itemDisplayName';
import { kitContentsOf, type KitComponentDetail, type KitSource } from '../kitContents';
import type { CatalogItem, ProductCategory } from '../types';
import { StockChip } from './StockChip';

const CATEGORY_ICON: Record<ProductCategory, LucideIcon> = {
    PHONE: Smartphone,
    SIM: CreditCard,
    MODEM: Router,
    ACCESSORY: Headphones,
    BUNDLE: Package,
};

const money = (cents: number): string => formatMXN(cents / 100);

/** Nombre visible del kit: el que se dio de alta antes que la marca y el modelo. */
export function kitNameOf(kit: KitSource): string {
    const description = kit.description?.trim() ?? '';
    return description !== '' ? description : itemDisplayName(kit);
}

/** Línea de texto de alto fijo: trunca con tooltip y, sin dato, reserva el renglón vacío. */
function RowLine({ text, className }: { text: string; className?: string }) {
    return (
        <div className={cn('h-[17px] truncate', className)} title={text !== '' ? text : undefined}>
            {text}
        </div>
    );
}

function ComponentRow({ component }: { component: KitComponentDetail }) {
    const Icon = component.category ? CATEGORY_ICON[component.category] : Package;
    const description = component.resolved ? component.description : 'Sin detalle';
    return (
        <li
            data-testid="kit-component"
            className="flex h-[78px] items-center gap-3 border-b border-border/60 last:border-b-0"
        >
            <span className="flex size-9 flex-none items-center justify-center rounded-md bg-muted text-muted-foreground">
                <Icon className="size-4" aria-hidden />
            </span>
            <div className="min-w-0 flex-1 text-[11.5px] text-muted-foreground">
                <RowLine
                    text={component.name}
                    className={cn(
                        'h-5 text-[13px] font-semibold text-foreground',
                        !component.resolved && 'font-mono',
                    )}
                />
                <RowLine text={description} />
                <RowLine text={component.facts.join(' · ')} />
            </div>
            <div className="flex flex-none flex-col items-end gap-1 tabular-nums">
                <span className="h-5 text-[13px] font-semibold">
                    {component.unitPriceCents !== null && money(component.unitPriceCents)}
                </span>
                <span className="text-[12px] text-muted-foreground">× {component.qty}</span>
                {component.resolved && component.stock !== null ? (
                    <StockChip item={{ stock: component.stock }} />
                ) : (
                    <span className="h-[18px]" />
                )}
            </div>
        </li>
    );
}

export function KitContentsModal({
    kit,
    items,
    discountedPriceCents = null,
    onClose,
    onChoose,
    chooseLabel = 'Elegir este kit',
    busy = false,
}: {
    /** Kit abierto; `null` cierra el modal. */
    kit: KitSource | null;
    /** Catálogo con que se resuelven los componentes. */
    items: readonly CatalogItem[];
    /** Precio con la promo de venta; `null` si ninguna lo baja. */
    discountedPriceCents?: number | null;
    onClose: () => void;
    /** Acción principal; sin ella el modal solo informa. */
    onChoose?: () => void;
    chooseLabel?: string;
    busy?: boolean;
}) {
    const contents = useMemo(
        () => (kit ? kitContentsOf(kit, items, discountedPriceCents) : null),
        [kit, items, discountedPriceCents],
    );

    return (
        <Modal
            isOpen={kit !== null}
            onClose={onClose}
            size="lg"
            title="Contenido del kit"
            description={kit ? kitNameOf(kit) : undefined}
        >
            {kit && contents && (
                <div className="flex flex-col gap-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex items-baseline gap-2 tabular-nums">
                            {contents.promoPriceCents !== null && (
                                <s className="text-[13px] text-muted-foreground">
                                    {money(contents.kitPriceCents)}
                                </s>
                            )}
                            <strong
                                data-testid="kit-price"
                                className={cn(
                                    'text-[20px]',
                                    contents.promoPriceCents !== null && 'text-success',
                                )}
                            >
                                {money(contents.promoPriceCents ?? contents.kitPriceCents)}
                            </strong>
                        </div>
                        {kit.stock !== undefined && <StockChip item={{ stock: kit.stock }} />}
                    </div>

                    {contents.components.length === 0 ? (
                        <p className="text-[12.5px] text-muted-foreground">
                            El catálogo no trae los componentes de este kit.
                        </p>
                    ) : (
                        <ul aria-label="Componentes del kit" className="flex list-none flex-col">
                            {contents.components.map((c) => (
                                <ComponentRow key={c.sku} component={c} />
                            ))}
                        </ul>
                    )}

                    <dl className="flex flex-col gap-1 rounded-lg bg-muted/50 p-3 text-[12.5px] tabular-nums">
                        {/* Solo cuando por separado sale más caro que el kit. */}
                        {contents.savingsCents !== null && contents.separateTotalCents !== null && (
                            <div className="flex justify-between gap-2">
                                <dt className="text-muted-foreground">Comprado por separado</dt>
                                <dd data-testid="separate-total">
                                    {money(contents.separateTotalCents)}
                                </dd>
                            </div>
                        )}
                        <div className="flex justify-between gap-2">
                            <dt className="text-muted-foreground">Precio del kit</dt>
                            <dd
                                className={cn(
                                    'font-semibold',
                                    contents.promoPriceCents !== null && 'text-success',
                                )}
                            >
                                {money(contents.promoPriceCents ?? contents.kitPriceCents)}
                            </dd>
                        </div>
                        {contents.savingsCents !== null && (
                            <div className="flex justify-between gap-2 font-semibold text-success">
                                <dt>Ahorras</dt>
                                <dd data-testid="savings">{money(contents.savingsCents)}</dd>
                            </div>
                        )}
                    </dl>

                    <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                        <Button variant="outline" onClick={onClose}>
                            Cerrar
                        </Button>
                        {onChoose && (
                            <Button isLoading={busy} onClick={onChoose}>
                                {chooseLabel}
                            </Button>
                        )}
                    </div>
                </div>
            )}
        </Modal>
    );
}
